import React, {
	Dispatch,
	RefAttributes,
	memo,
	useCallback,
	useMemo,
	useState,
} from "react";
import { Select, react } from "uikit";
import { useTranslation } from "react-i18next";

import PaymentSystem, {
	MaibPaymentCountryIso,
} from "../../../../../../../../services/PaymentSystem";
import useObjectEditor from "../../../../../../../../hooks/useObjectEditor";
import translationPath from "../../../../constants/translationPath";

import InternalController from "./Controller";

const MaibPaymentsCountryIsoBase = react.withController<
	MaibPaymentsCountryIso.PropsBase,
	MaibPaymentsCountryIso.Controller
>(({ value, onChange, controller }) => {
	const [error, setError] = useState<boolean>(false);

	const { t } = useTranslation();

	const valueEditor = useObjectEditor(value, onChange);

	const maibPaymentsCountryIso = valueEditor.useGetter(
		"maibPaymentsCountryIso",
	);
	const maibPaymentsCountryIsoSet = valueEditor.useSetter(
		"maibPaymentsCountryIso",
	);

	const selectOptionsWrap = useCallback(
		(item: {
			id: MaibPaymentCountryIso;
			label: MaibPaymentCountryIso;
		}) => ({
			key: item.id,
			value: item.id,
			label: item.label,
		}),
		[],
	);

	const options = useMemo(
		() =>
			Object.values(MaibPaymentCountryIso).map((label) =>
				selectOptionsWrap({
					id: label,
					label,
				}),
			),
		[selectOptionsWrap],
	);

	const updateSelectedValues = useCallback(
		(value) => {
			maibPaymentsCountryIsoSet(value);
			setError(false);
		},
		[maibPaymentsCountryIsoSet, setError],
	);

	controller.setContext({ value, setError });

	const textError = useMemo(() => {
		if (!error) return "";
		return t([
			`${translationPath}.errors.countryIso`,
			"Country not selected",
		]);
	}, [t, error]);

	const placeholder = useMemo(
		() =>
			t([
				`${translationPath}.placeholders.countryIso`,
				"Select a country",
			]),
		[t],
	);

	return (
		<Select
			style={{ width: "200px" }}
			disabled={options.length === 1}
			placeholder={placeholder}
			error={textError}
			value={maibPaymentsCountryIso}
			options={options}
			onChange={updateSelectedValues}
		/>
	);
}, InternalController);
const MaibPaymentsCountryIso = memo(MaibPaymentsCountryIsoBase);

declare namespace MaibPaymentsCountryIso {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: Value;
		onChange: Dispatch<Value>;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	interface Value extends PaymentSystem.Model {}
}

export default MaibPaymentsCountryIso;
